import { ExternalLink } from "lucide-react";
import Seo from "../../components/Seo";

export default function FairHousing() {
  return (
    <div className="bg-stone-50 min-h-screen" data-testid="fair-housing-page">
      <Seo
        title="Fair Housing"
        description="Gens Realty, LLC and Edge Home Finance proudly comply with all federal and state Fair Housing laws. Equal Housing Opportunity."
        path="/fair-housing"
      />
      <article className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-24">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-3">Legal</div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter">Fair Housing</h1>
        <p className="text-[#555] mt-5 text-lg max-w-2xl">
          Gens Realty, LLC and Edge Home Finance Corporation are committed to ensuring fair housing for all.
        </p>

        {/* EHO badge */}
        <div className="mt-10 flex items-center gap-5 bg-white border border-gray-200 rounded-2xl p-6">
          <svg viewBox="0 0 64 64" className="w-16 h-16 shrink-0" aria-label="Equal Housing Opportunity">
            <rect x="2" y="2" width="60" height="60" rx="4" fill="none" stroke="#1A1A1A" strokeWidth="2" />
            <path d="M32 12 L52 30 L46 30 L46 50 L36 50 L36 38 L28 38 L28 50 L18 50 L18 30 L12 30 Z" fill="#1A1A1A"/>
            <text x="32" y="60" textAnchor="middle" fontSize="6" fill="#1A1A1A" fontFamily="sans-serif">EQUAL HOUSING</text>
          </svg>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Equal Housing Opportunity</div>
            <p className="font-display text-xl font-semibold text-[#1A1A1A] mt-1">
              We do business in accordance with the Federal Fair Housing Law.
            </p>
          </div>
        </div>

        <div className="mt-10 space-y-8 text-[#333] leading-relaxed">
          <Section title="The Federal Fair Housing Act">
            <p>
              The Fair Housing Act (Title VIII of the Civil Rights Act of 1968, as amended) prohibits
              discrimination in the sale, rental, advertising, and financing of housing on the basis of:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Race</li>
              <li>Color</li>
              <li>Religion</li>
              <li>National origin</li>
              <li>Sex (including gender identity and sexual orientation)</li>
              <li>Familial status</li>
              <li>Disability / handicap</li>
            </ul>
          </Section>

          <Section title="Texas Fair Housing Act">
            <p>
              The Texas Fair Housing Act (Texas Property Code, Chapter 301) provides parallel protections
              under state law. Discriminatory housing practices are prohibited in Texas as well.
            </p>
          </Section>

          <Section title="Our commitment">
            <p>
              Gens Realty, LLC and Edge Home Finance Corporation are committed to providing equal
              opportunity in all aspects of real estate brokerage, mortgage origination, and ancillary
              services. Every prospective buyer, seller, borrower, or tenant is treated with respect and
              without regard to any protected classification listed above.
            </p>
            <p>
              We will not knowingly accept any agreement for the sale or rental of housing, or for
              mortgage financing, that involves unlawful discrimination.
            </p>
          </Section>

          <Section title="Disability accommodations">
            <p>
              If you require reasonable accommodation to access our website, view a property, or complete
              an application — including alternative formats, sign-language interpreting, or in-person
              assistance — please contact us at{" "}
              <a href="mailto:PJoseph@gensrealty.com" className="text-[#E63946] underline">PJoseph@gensrealty.com</a>{" "}
              or (832) 741-4662. We will do our best to meet your needs.
            </p>
          </Section>

          <Section title="Reporting a fair housing concern">
            <p>If you believe you have experienced housing discrimination, you may:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                File a complaint with the U.S. Department of Housing and Urban Development (HUD):{" "}
                <a
                  href="https://www.hud.gov/program_offices/fair_housing_equal_opp/online-complaint"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E63946] underline inline-flex items-center gap-1"
                >
                  hud.gov complaint portal <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                File a complaint with the Texas Workforce Commission, Civil Rights Division:{" "}
                <a
                  href="https://www.twc.texas.gov/businesses/civil-rights"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E63946] underline inline-flex items-center gap-1"
                >
                  twc.texas.gov <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                Contact us directly at{" "}
                <a href="mailto:PJoseph@gensrealty.com" className="text-[#E63946] underline">
                  PJoseph@gensrealty.com
                </a>{" "}
                — concerns will be escalated to the Broker.
              </li>
            </ul>
          </Section>

          <Section title="TREC Information About Brokerage Services (IABS) & Consumer Protection Notice">
            <p>
              Texas law requires real estate license holders to give the following notices to all parties
              for whom they are providing services:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                Texas Real Estate Commission Information About Brokerage Services (IABS):{" "}
                <a
                  href="https://www.trec.texas.gov/forms/information-about-brokerage-services"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E63946] underline inline-flex items-center gap-1"
                >
                  trec.texas.gov IABS <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                TREC Consumer Protection Notice:{" "}
                <a
                  href="https://www.trec.texas.gov/forms/consumer-protection-notice"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E63946] underline inline-flex items-center gap-1"
                >
                  trec.texas.gov CPN <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </Section>

          <Section title="Mortgage NMLS information">
            <p>
              Mortgage origination services are provided by{" "}
              <strong>Edge Home Finance Corporation</strong>, NMLS #891464. Loan Officer:{" "}
              <strong>Pierre Joseph</strong>, NMLS #1978260. Verify licensing at{" "}
              <a
                href="https://www.nmlsconsumeraccess.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E63946] underline inline-flex items-center gap-1"
              >
                NMLS Consumer Access <ExternalLink className="w-3 h-3" />
              </a>
              .
            </p>
            <p>Equal Housing Opportunity Lender.</p>
          </Section>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-semibold text-[#1A1A1A] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
